import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Helmet } from 'react-helmet-async'

import { useAccount, useProduct } from 'src/hooks'

import { FullWidthSection, Loader, PageFooter, PageWrapper, ProductImage } from 'src/components'
import { ProductCard } from 'src/components/product-card'
import { LeftDivider, TopDivider } from './product.styled'
import { LayoutContainer } from 'src/components/layout-container'
import { Typography, useTheme } from '@mui/material'
import { FlaskIcon } from 'src/assets/icons/v2/FlaskIcon'
import { getBaseUrl, toAbs } from 'src/utils/base-url.util'
import { JOURNEY_PAGE_TEMPLATE_ID } from 'src/templates/constants'
import { FairtradeProductTemplate } from 'src/templates/fairtrade/product'

export default function ProductPage() {
  const [t, i18n] = useTranslation()
  const { productId, simpleProductId } = useParams()
  const theme = useTheme()
  const { data: product, isLoading: isProductLoading } = useProduct(
    productId,
    i18n.language,
    simpleProductId,
  )

  const { data: account, isLoading: isAccountLoading } = useAccount(
    product?.account_id,
    i18n.language,
  )

  if (!product || !account || isAccountLoading || isProductLoading) {
    return <Loader />
  }

  // --- SEO bits ---

  const base = getBaseUrl()
  const canonical = `${base}${window.location.pathname}`
  const image = product?.image ? toAbs(product.image) : `${base}/android-chrome-512x512.png`

  const holder = account?.account_holder ?? 'the brand'
  const productTitle = `${product.name} – Trackgood Journey`
  const productDescription = `Discover ${product.name} from ${holder} on Trackgood Journey.`

  // const other = product?.impacts?.flatMap((i) => {
  //   return i.un_sdg
  // })

  const isFairtradeTemplate =
    account?.journey_page_template_id === JOURNEY_PAGE_TEMPLATE_ID.FAIRTRADE

  return (
    <LayoutContainer>
      <Helmet>
        {/* Title & description */}
        <title id="index__pages-product__title__l51">{productTitle}</title>
        <meta
          id="index__pages-product__meta__l52"
          name="description"
          content={productDescription}
        />

        <link id="index__pages-product__link__l53" rel="canonical" href={canonical} />

        <meta id="index__pages-product__meta__l55" property="og:title" content={productTitle} />
        <meta
          id="index__pages-product__meta__l56"
          property="og:description"
          content={productDescription}
        />

        <meta id="index__pages-product__meta__l57" property="og:url" content={canonical} />
        <meta id="index__pages-product__meta__l58" property="og:image" content={image} />

        <meta id="index__pages-product__meta__l60" name="twitter:title" content={productTitle} />
        <meta
          id="index__pages-product__meta__l61"
          name="twitter:description"
          content={productDescription}
        />

        <meta id="index__pages-product__meta__l62" name="twitter:image" content={image} />
      </Helmet>

      {!isFairtradeTemplate && (account?.alternative_color || product?.ai_generated) && (
        <TopDivider id="index__pages-product__topdivider__l66" bgColor={account.alternative_color}>
          {product?.ai_generated && (
            <>
              <Typography
                id="index__pages-product__typography__l69"
                sx={{
                  fontSize: '10px',
                  fontWeight: 700,
                  color: theme.palette.common.white,
                  mt: '2px',
                }}
                textAlign={'center'}
              >
                {t('ai-generated-label')}
              </Typography>
              <FlaskIcon size={14} />
            </>
          )}
        </TopDivider>
      )}
      {isFairtradeTemplate && (
        <LeftDivider
          id="index__pages-product__leftdivider__l104"
          bgColor={account?.alternative_color || account?.primary_color}
        />
      )}

      {isFairtradeTemplate ? (
        <FairtradeProductTemplate product={product} account={account} />
      ) : (
        <PageWrapper>
          <FullWidthSection
            id="index__pages-product__fullwidthsectionst__l101"
            idInner="index__pages-product__fullwidthsectioninner__l101"
          >
            <ProductImage
              name={product.name}
              image={product.image}
              media={product.mainMedia}
              unSdg={account?.show_unsgd_home_page ? product.un_sdg : []}
            />
          </FullWidthSection>

          <FullWidthSection
            id="index__pages-product__fullwidthsectionst__l102"
            idInner="index__pages-product__fullwidthsectioninner__l102"
          >
            <ProductCard product={product} primaryColor={account.primary_color} />
          </FullWidthSection>
        </PageWrapper>
      )}

      <PageFooter />
    </LayoutContainer>
  )
}
